import React, { useContext, useState, useCallback, useEffect } from "react"
import { useHistory } from "react-router-dom"
import { useTranslation } from "react-i18next";

import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { v4 } from "uuid"

import {
    SortableTreeWithoutDndContext as SortableTree,
    changeNodeAtPath,
    toggleExpandedForAll,
    removeNodeAtPath,
    addNodeUnderParent,
    getNodeAtPath,
} from "react-sortable-tree";

import { CircularProgress } from "@material-ui/core";
import { createMuiTheme, ThemeProvider } from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";
import DoubleArrowIcon from '@material-ui/icons/DoubleArrow';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';

import { listSorter } from "../utils"
import { walk, translateIncomeTreeData } from "../RuleTree/treeUtils";
import AuthContext from "../../../context/auth-context";
import RulesContext from "../../../context/rules-context"
import { CreateUserVariables } from "../RuleTree/CreateUserVariables/CreateUserVariables";
import FilterText from "../../@GeneralComponents/FilterText";
import BoxLibrary from "../RuleTree/EngineUtilsComponents/BoxLibrary"
import BoxConfiguration from "../RuleTree/EngineUtilsComponents/BoxConfiguration";
import Button from "../../@GeneralComponents/Button";
import ExpandIcon from '../../../assets/rulesIcon/expand.svg'
import MinimizeIcon from '../../../assets/rulesIcon/minimize.svg'
import StandardNodeComponent from "../RuleTree/EngineUtilsComponents/StandardNodeComponent";
import { getTypeComponents } from "../RuleTree/BoxTypeMapper";
import SaveRulePieceDialog from "./Dialogs/SaveRulePieceDialog";
import NodeRemovalConfirmation from "../RuleTree/Dialogs/NodeRemovalConfirmation";

const theme = createMuiTheme({
    palette: {
        primary: {
            main: "#0b1f82",
        },
    },
});

const nodeGeneralTypes = "GeneralNode";

const RulePieceTreeEngine = (props) => {

    let { rulePieceDefinition } = props
    let history = useHistory()
    const { t } = useTranslation();

    let user_data = useContext(AuthContext).user_data;
    let isViewer = user_data.roles.includes('rule_piece_viewer')
    let isEditor = user_data.roles.includes('rule_piece_creator') || user_data.includes('rule_piece_editor')

    const invalidTreeText = t("A árvore está inválida, deve possuir pelo menos um nó e não deve haver erros.")

    let rulePieceContext = useContext(RulesContext);

    const [userVariablesAvailable, setUserVariablesAvailable] = useState(null)
    const [treeState, setTreeState] = useState(null);
    const [invalidTree, setInvalidTree] = useState(undefined);

    const [boxConfiguration, setBoxConfiguration] = useState({ open: false });
    const [libraryExpanded, setLibraryExpanded] = useState(true);
    const [duplicateSnackbarOpen, setDuplicateSnackbarOpen] = useState(false);
    const [saveRulePieceDialog, setSaveRulePieceDialog] = useState(false);

    const [filters, setFilters] = useState({
        search_component: ""
    })

    const [removalConfirmationDialog, setRemovalConfirmationDialog] = useState({
        open: false,
    });

    const getNodeKey = ({ node }) => node.id;

    const doTreeDataRequest = useCallback(() => {
        let initialTreeData = translateIncomeTreeData(
            rulePieceDefinition.tree_data,
            rulePieceContext,
            userVariablesAvailable
        )
        setTreeState({ treeData: initialTreeData });
        let newTreeData = walk({
            treeData: initialTreeData,
            getNodeKey: getNodeKey,
            ignoreCollapsed: false,
        });
        if ((newTreeData || { anyError: true }).anyError) setInvalidTree(true);
        else setInvalidTree(false);
    }, [rulePieceDefinition, rulePieceContext, userVariablesAvailable])

    useEffect(() => {
        if (rulePieceDefinition) {
            setUserVariablesAvailable(listSorter(rulePieceDefinition.user_variables, "variable_name"))
        }
    }, [rulePieceDefinition])

    useEffect(() => {
        if (userVariablesAvailable && !treeState) {
            doTreeDataRequest()
        }
    }, [userVariablesAvailable, treeState, doTreeDataRequest]);

    const handleNodeChange = (node) => {
        let new_tree_data = changeNodeAtPath({
            treeData: treeState.treeData,
            path: node.path,
            getNodeKey: getNodeKey,
            newNode: node.node,
        });
        goRecursively(new_tree_data);
    };

    const deleteNodeFromTree = (node) => {
        let new_tree_data = removeNodeAtPath({
            treeData: treeState.treeData,
            path: node.path,
            getNodeKey: getNodeKey,
            newNode: node.node,
        });

        if (boxConfiguration.node) {
            let existentOpenNode = getNodeAtPath({
                treeData: new_tree_data,
                path: boxConfiguration.node.path,
                getNodeKey: getNodeKey,
            });
            if (!existentOpenNode) handleConfigurationClose();
        }
        goRecursively(new_tree_data);
    };

    const handleConfigurationClose = () => {
        setBoxConfiguration({ open: false });
    };

    const goRecursively = (treeData) => {
        handleConfigurationClose();
        let newTreeData = walk({
            treeData: treeData,
            getNodeKey: getNodeKey,
            ignoreCollapsed: false,
        });
        if ((newTreeData || { anyError: true }).anyError) setInvalidTree(true);
        else setInvalidTree(false);
        setTreeState({ treeData: (newTreeData || { treeData: [] }).treeData });
    };

    const handleExpand = (event) => {
        handleConfigurationClose();
        setLibraryExpanded(!libraryExpanded);
    };

    const handleNodeExpansion = (expanded) => {
        let newTreeData = toggleExpandedForAll({
            treeData: treeState.treeData,
            expanded,
        });
        setTreeState({ treeData: newTreeData });
    };

    const handleConfigureNodeComponent = (node) => {
        return <StandardNodeComponent {...node} />;
    };

    const myGenerateNode = (node) => {
        let changeNodeInfo = {};
        let is_active = false;
        if (boxConfiguration.node) {
            if (boxConfiguration.node.node.id === node.node.id) {
                is_active = true;
            }
        }
        if (is_active) {
            changeNodeInfo.className = "nodeActive";
        } else if (node.node.on_error) {
            changeNodeInfo.className = "rulesError";
        }

        changeNodeInfo.title = getTypeComponents({
            type: node.node.type,
            handleConfigurationOpen: handleConfigurationOpen,
            handleNodeRemove: handleNodeRemove,
            handleNodeDuplication: handleNodeDuplication,
            userVariablesAvailable: userVariablesAvailable,
            node: node
        }).box_component;

        return changeNodeInfo;
    };

    const handleConfigurationOpen = (node) => {
        setLibraryExpanded(true);
        goRecursively(treeState.treeData);
        setBoxConfiguration({ open: true, node: node });
    };

    const handleNodeRemove = (node) => {
        setRemovalConfirmationDialog({ open: true, node: node });
    };

    const handleNodeDuplication = (node) => {

        let node_deep_copy = deepcopy_node(node.node);
        let updated_node = cycle_node_ids(node_deep_copy);

        let original_node_id = node.node.id
        let updated_node_id = updated_node.id

        node.node.expanded = false
        updated_node.expanded = false


        let new_tree_data = addNodeUnderParent({
            treeData: treeState.treeData,
            parentKey: node.path[node.path.length - 2],
            expandParent: true,
            getNodeKey: getNodeKey,
            newNode: updated_node,
            addAsFirstChild: true,
        }).treeData

        reorder_siblings(new_tree_data, node.path, original_node_id, updated_node_id);

        if (boxConfiguration.node) {
            let existentOpenNode = getNodeAtPath({
                treeData: new_tree_data,
                path: boxConfiguration.node.path,
                getNodeKey: getNodeKey,
            });
            if (!existentOpenNode) handleConfigurationClose();
        }
        goRecursively(new_tree_data);
        setDuplicateSnackbarOpen(true);
    }

    function deepcopy_node(original_node) {
        let prototypeDescriptors = Object.getOwnPropertyDescriptors(Object.getPrototypeOf(original_node));
        let protoClone = Object.create(null, prototypeDescriptors);
        let node_deep_copy = Object.create(protoClone, Object.getOwnPropertyDescriptors(original_node));

        if (original_node.children) {
            node_deep_copy.children = original_node.children.map((child) => {
                return deepcopy_node(child);
            });
        }

        return node_deep_copy;
    }

    function cycle_node_ids(input_node) {
        let new_uuid = v4();
        let updated_node = { ...input_node, id: new_uuid };

        if (input_node.children) {
            updated_node.children = input_node.children.map((child, i) => {
                return cycle_node_ids(child);
            });
        }

        return updated_node;
    }

    function reorder_siblings(tree_data, node_path, original_node_id, updated_node_id) {

        let parent_node = getNodeAtPath({
            treeData: tree_data,
            path: node_path.slice(0, node_path.length - 1),
            getNodeKey: getNodeKey,
        }).node;

        for (let i = 0; i < parent_node.children.length; i++) {
            if (parent_node.children[i].id === updated_node_id) {
                for (let j = 0; j < parent_node.children.length; j++) {
                    if (parent_node.children[j].id === original_node_id) {
                        let deep_copied_updated_node = deepcopy_node(parent_node.children[i]);
                        parent_node.children.splice(i, 1);
                        parent_node.children.splice(j, 0, deep_copied_updated_node);
                        return
                    }
                }
            }
        }
    }

    const buttonsDisplayHandler = () => {
        let buttonsDisplay = {
            go_back: {
                show: false
            },
            save: {
                show: false,
                disabled: false
            }
        }

        if (isEditor) {
            buttonsDisplay.go_back.show = true
            buttonsDisplay.save.show = true
        } else if (isViewer) {
            buttonsDisplay.go_back.show = true
            buttonsDisplay.save.show = false
        }

        if (invalidTree) {
            buttonsDisplay.save.disabled = true
        }

        return buttonsDisplay
    }

    const buttonsDisplay = buttonsDisplayHandler()

    const returnUpperRightBoxes = () => {
        return (
            <div style={{ display: "flex", margin: "auto 0 auto auto" }}>
                {buttonsDisplay.go_back.show &&
                    <Button
                        button_option="standard_onlyboarder"
                        buttonLabel="go_back"
                        onClick={() => history.push("/rule-pieces-inbox")}
                        buttonStyle={{ minWidth: "100px", whiteSpace: "nowrap" }}
                    />
                }
                {buttonsDisplay.save.show &&
                    <Button
                        tooltip={buttonsDisplay.save.disabled ? invalidTreeText : null}
                        button_option={buttonsDisplay.save.disabled ? "standard_disabled" : "standard"}
                        buttonLabel="save"
                        onClick={() => buttonsDisplay.save.disabled ? null : setSaveRulePieceDialog(true)}
                        buttonStyle={{ minWidth: "100px", whiteSpace: "nowrap" }}
                    />
                }
            </div>
        )
    }

    if (userVariablesAvailable && treeState) {
        return (
            <DndProvider backend={HTML5Backend}>
                <ThemeProvider theme={theme}>
                    <div className="ruleEnginePage">
                        {libraryExpanded ? (
                            <div
                                className="ruleEngineSidebar"
                                style={{ width: "fit-content", padding: 16 }}
                            >
                                <div>
                                    <CreateUserVariables
                                        treeState={treeState}
                                        userVariablesAvailable={userVariablesAvailable}
                                        setUserVariablesAvailable={setUserVariablesAvailable}
                                    />
                                    <div className="internalCardContainer">
                                        <div className={["blueText", "subtitleSize",].join(" ")}>
                                            {t("components")}
                                        </div>
                                        <div style={{ width: "100%" }}>
                                            <div className={["normalText", "normalSmallSize", "justifyText"].join(" ")} style={{ wordWrap: "break-word", position: "relative" }}>
                                                {t("add_component_description")}
                                            </div>
                                        </div>
                                    </div>
                                    <FilterText
                                        id={"search_component"}
                                        labelValue={t("search_component")}
                                        filters={filters}
                                        setFilters={setFilters}
                                        icon={<SearchIcon style={{ width: "15px", height: "15px" }} />}
                                    />
                                </div>
                                <BoxLibrary searchString={filters.search_component} userVariablesAvailable={userVariablesAvailable} />
                                {boxConfiguration.open &&
                                    <BoxConfiguration
                                        node={boxConfiguration.node}
                                        handleNodeChange={handleNodeChange}
                                        handleConfigurationClose={handleConfigurationClose}
                                        read_only={false}
                                        userVariablesAvailable={userVariablesAvailable}
                                    />}
                            </div>
                        ) : (
                            <div>
                                {boxConfiguration.open && (
                                    <BoxConfiguration
                                        node={boxConfiguration.node}
                                        handleConfigurationClose={handleConfigurationClose}
                                        read_only={false}
                                        userVariablesAvailable={userVariablesAvailable}
                                    />
                                )}
                            </div>
                        )}
                        <div
                            style={{
                                padding: "0px 30px",
                                flexGrow: "2",
                                flexDirection: "column",
                                display: "flex",
                                width: "100%"
                            }}
                        >
                            <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
                                <div>
                                    <div
                                        className={["blueText", "titleSize"].join(" ")}
                                        style={{ margin: "10px 0px" }}
                                    >
                                        {t("Trecho - ") + t(rulePieceDefinition.name)}
                                    </div>
                                </div>
                                <div className="buttonRowWrapper">
                                    <Button
                                        button_option="standard"
                                        buttonLabel=""
                                        onClick={handleExpand}
                                        buttonStyle={{ padding: "10px", margin: "auto 0" }}
                                    >
                                        <DoubleArrowIcon style={{ height: "15px", width: "15px", scale: "1.3", transform: libraryExpanded ? "scaleX(-1)" : null }} />
                                    </Button>
                                    <Button
                                        button_option="standard"
                                        buttonLabel=""
                                        onClick={() => handleNodeExpansion(true)}
                                        buttonStyle={{ padding: "10px", margin: "auto 0" }}
                                    >
                                        <img src={ExpandIcon} alt="Expand Icon" style={{ height: "15px", width: "15px", scale: "1.3" }} />
                                    </Button>
                                    <Button
                                        button_option="standard"
                                        buttonLabel=""
                                        onClick={() => handleNodeExpansion(false)}
                                        buttonStyle={{ padding: "10px", margin: "auto 0" }}
                                    >
                                        <img src={MinimizeIcon} alt="Minimize Icon" style={{ height: "15px", width: "15px", scale: "1.3" }} />
                                    </Button>
                                    {returnUpperRightBoxes()}
                                </div>
                            </div>
                            <div style={{ flexGrow: "2" }}>
                                <SortableTree
                                    treeData={treeState.treeData}
                                    onChange={(treeData) => goRecursively(treeData)}
                                    dndType={nodeGeneralTypes}
                                    nodeContentRenderer={handleConfigureNodeComponent}
                                    getNodeKey={getNodeKey}
                                    generateNodeProps={(node) => myGenerateNode(node)}
                                />
                            </div>
                        </div>
                    </div>
                    <SaveRulePieceDialog
                        open={saveRulePieceDialog}
                        onClose={() => setSaveRulePieceDialog(false)}
                        rulePieceDefinition={rulePieceDefinition}
                        treeData={treeState.treeData}
                        userVariablesAvailable={userVariablesAvailable}
                    />
                    {removalConfirmationDialog.node ? (
                        <NodeRemovalConfirmation
                            open={removalConfirmationDialog.open}
                            onClose={() => setRemovalConfirmationDialog({ open: false })}
                            node={removalConfirmationDialog.node}
                            deleteNodeFromTree={deleteNodeFromTree}
                        />
                    ) : null}

                </ThemeProvider>
                <Snackbar
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    open={duplicateSnackbarOpen}
                    autoHideDuration={5000}
                    onClose={() => setDuplicateSnackbarOpen(false)}
                >
                    <Alert
                        variant="standard"
                        onClose={() => { setDuplicateSnackbarOpen(false) }}
                        severity="warning"
                    >
                        <AlertTitle>Nó duplicado</AlertTitle>
                        Lembre-se de revisá-lo antes de salvar a regra.
                    </Alert>
                </Snackbar>
            </DndProvider>
        );
    } else {
        return (
            <div style={{ height: "100%", display: "flex" }}>
                <CircularProgress style={{ margin: "auto" }} />
            </div>
        );
    }
}


export default RulePieceTreeEngine;